import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
  Shield, DollarSign, Activity, TrendingDown, Database, RefreshCw,
  Zap, Users, ArrowUpRight, ArrowDownRight, Building2, Landmark
} from 'lucide-react';
import { RevenueDashboard } from './RevenueDashboard';
import { EngagementDashboard } from './EngagementDashboard';
import { ComplianceDashboard } from './ComplianceDashboard';
import { RegulatoryWatchCard } from './RegulatoryWatch';
import { NachaDashboard } from './NachaDashboard';
import { SecurityAlertBadge } from './SecurityAlertBadge';
import { SecurityScoreCard, ThreatMapCard } from './SecurityScoreAndThreatMap';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTab = ({ token, businessId }) => {
  const [usage, setUsage] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  useEffect(() => {
    if (!token) return;
    fetchUsage();
  }, [token, period]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const [usageRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/admin/plaid-usage?days=${period}`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/plaid-usage/top-users?days=${period}`, authHeaders)
      ]);
      setUsage(usageRes.data);
      setTopUsers(usersRes.data.top_users || []);
    } catch (err) {
      console.error('Failed to load admin data:', err);
    }
    setLoading(false);
  };

  const COLORS = ['#0A2B1D', '#3A6E53', '#6BA38A', '#9CC4AF', '#DC2626', '#F59E0B'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E6E8E6] rounded-sm p-3 shadow-lg text-xs">
        <p className="font-medium text-[#111614] mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' && p.name.includes('Cost') ? fmt(p.value) : p.value}</p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="admin-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-[#0A2B1D]" />
        <span className="ml-3 text-[#5C6661]">Loading admin dashboard...</span>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="text-center py-20 text-[#5C6661]" data-testid="admin-error">
        <Shield className="w-12 h-12 mx-auto mb-3 text-[#B4C9BE]" />
        <p>Failed to load usage data. Admin access required.</p>
      </div>
    );
  }

  const s = usage.summary;

  return (
    <div className="space-y-6" data-testid="admin-tab">
      {/* Security Audit Alert Badge — surfaces unresolved daily/weekly findings */}
      <SecurityAlertBadge
        token={token}
        onJumpToCompliance={() => {
          const el = document.getElementById('compliance-dashboard-anchor');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      {/* Security Score Dashboard */}
      <SecurityScoreCard token={token} />

      {/* Threat Map */}
      <ThreatMapCard token={token} />

      {/* Revenue Dashboard */}
      <RevenueDashboard token={token} />

      {/* Divider */}
      <div className="border-t border-[#E6E8E6] pt-2" />

      {/* Engagement Dashboard */}
      <EngagementDashboard token={token} />

      {/* Divider */}
      <div className="border-t border-[#E6E8E6] pt-2" />

      {/* Security & Compliance */}
      <div id="compliance-dashboard-anchor" className="scroll-mt-24">
        <ComplianceDashboard token={token} />
      </div>

      {/* Divider */}
      <div className="border-t border-[#E6E8E6] pt-2" />

      {/* Regulatory Watch — daily federal/state feed monitoring */}
      <RegulatoryWatchCard token={token} />

      {/* Divider */}
      <div className="border-t border-[#E6E8E6] pt-2" />

      {/* NACHA ACH Compliance */}
      <NachaDashboard token={token} businessId={businessId} />

      {/* Divider */}
      <div className="border-t border-[#E6E8E6] pt-2" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#0A2B1D]" />
          <h2 className="text-lg font-semibold text-[#111614]">Plaid Usage Dashboard</h2>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant={period === d ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(d)}
              className={period === d ? "bg-[#0A2B1D] text-white" : ""}
              data-testid={`period-${d}-btn`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#0A2B1D]" data-testid="total-calls-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Total API Calls</span>
              <Activity className="w-4 h-4 text-[#0A2B1D]" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{s.total_calls.toLocaleString()}</p>
            <p className="text-xs text-[#8A948F] mt-1">{s.live_calls} live / {s.cached_calls} cached</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500" data-testid="cost-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Estimated Cost ({period}d)</span>
              <DollarSign className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{fmt(s.total_estimated_cost)}</p>
            <p className="text-xs text-[#8A948F] mt-1">Projected: {fmt(s.monthly_projection)}/mo</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-400" data-testid="savings-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Cache Savings</span>
              <TrendingDown className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-600">{fmt(s.cost_savings_from_cache)}</p>
            <p className="text-xs text-[#8A948F] mt-1">{s.cache_hit_rate}% cache hit rate</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-400" data-testid="connections-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Active Connections</span>
              <Landmark className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{s.active_connections}</p>
            <p className="text-xs text-[#8A948F] mt-1">{s.unique_users} users / {s.unique_businesses} businesses</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Usage Chart */}
      {usage.daily_breakdown?.length > 0 && (
        <Card data-testid="daily-usage-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#0A2B1D]" />
              Daily API Calls & Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={usage.daily_breakdown} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#8A948F' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => v.slice(5)}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="live" name="Live Calls" fill="#0A2B1D" radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="left" dataKey="cached" name="Cached Calls" fill="#9CC4AF" radius={[2, 2, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="cost" name="Cost ($)" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Breakdown + Top Users */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Endpoint Breakdown */}
        <Card data-testid="endpoint-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-4 h-4 text-[#0A2B1D]" />
              API Endpoint Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usage.endpoint_breakdown?.length > 0 ? (
              <div className="space-y-3">
                {usage.endpoint_breakdown.map((ep, i) => (
                  <div key={ep.endpoint} className="bg-[#F6F5F2] rounded-sm p-3" data-testid={`endpoint-${ep.endpoint}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#111614] font-mono">{ep.endpoint}</span>
                      <span className="text-sm font-semibold text-[#0A2B1D]">{fmt(ep.estimated_cost)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#8A948F]">
                      <span>{ep.total_calls} total</span>
                      <span className="text-green-600">{ep.cached_calls} cached</span>
                      <span>{ep.live_calls} live</span>
                      <span className="text-emerald-600">{ep.cache_rate}% hit rate</span>
                    </div>
                    {/* Mini progress bar showing cache ratio */}
                    <div className="mt-2 h-1.5 bg-[#E6E8E6] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${ep.cache_rate}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#8A948F] py-8">No API calls recorded yet</p>
            )}
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card data-testid="top-users">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-[#0A2B1D]" />
              Top API Consumers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topUsers.length > 0 ? (
              <div className="space-y-3">
                {topUsers.map((user, i) => (
                  <div key={user.business_id || `user-${i}`} className="flex items-center justify-between bg-[#F6F5F2] rounded-sm p-3" data-testid={`top-user-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0A2B1D] flex items-center justify-center text-white text-xs font-bold">
                        {i + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#111614]">{user.name || user.email}</p>
                        <p className="text-xs text-[#8A948F]">{user.calls} calls &middot; {user.cache_rate}% cached</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#0A2B1D]">{fmt(user.cost)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#8A948F] py-8">No user data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost Protection Status */}
      <Card className="border-[#E2ECE7] bg-[#F6F5F2]" data-testid="cost-protection-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-600" />
            Cost Protection Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 bg-white rounded-sm p-3 border border-[#E6E8E6]">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#111614]">Balance Cache</p>
                <p className="text-xs text-[#5C6661]">15-minute TTL active. Saves ~{fmt(0.10)}/duplicate call.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-sm p-3 border border-[#E6E8E6]">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#111614]">Sync Cooldown</p>
                <p className="text-xs text-[#5C6661]">1-hour minimum between transaction syncs per business.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-sm p-3 border border-[#E6E8E6]">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#111614]">Frontend Guards</p>
                <p className="text-xs text-[#5C6661]">Refresh button disabled during cooldown with countdown timer.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTab;
